import { z } from "zod";

const columnSourceElement = z.record(z.string(), z.string().nullable());

export const jsonScalarType = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);

const BaseUnit = z.object({
  unit: z.string(),
  value: jsonScalarType,
});

export function isBaseUnit(value: unknown): value is z.infer<typeof BaseUnit> {
  return BaseUnit.safeParse(value).success;
}

const tableExtractionRow = z.object({
  data: z.record(z.union([jsonScalarType, BaseUnit])),
  metadata: z.record(z.union([jsonScalarType, columnSourceElement])),
});

export const valueExtraction = z.object({
  attributeId: z.string(),
  value: jsonScalarType,
  metadata: z.record(jsonScalarType),
});

const extractionData = z.object({
  extractedAt: z.string(),
  extractionId: z.string(),
});

const fileData = z.object({
  fileId: z.string().nullable(),
  fileName: z.string(),
  fileUri: z.string().nullable(),
});

const documentData = z.object({
  tables: z.record(z.string(), z.array(tableExtractionRow)),
  valueGroups: z.record(z.string(), z.array(valueExtraction)),
});

const userInfo = z.object({
  userId: z.string(),
  email: z.string().nullable(),
});

const Coordinate = z.tuple([z.number(), z.number()]);

const documentElement = z.object({
  id: z.string(),
  parentId: z.string(),
  stringContent: z.string(),
  isRelevantJudgement: z.boolean().nullable(),
  isRelevantConfidence: z.enum(["high", "medium", "low"]).nullable(),
  contentTokenCount: z.number().nullable(),
});

const pdfElement = documentElement.extend({
  startPageNumber: z.number(),
  boundingBox: z
    .tuple([Coordinate, Coordinate, Coordinate, Coordinate])
    .nullable(),
  span: z.tuple([z.number(), z.number()]),
});

const pdfTableCell = z.object({
  content: z.union([z.string(), z.number()]),
  boundingBox: z
    .tuple([Coordinate, Coordinate, Coordinate, Coordinate])
    .nullable(),
});

export const pdfTable = pdfElement.extend({
  headers: z.array(z.string()).nullable(),
  cells: z.array(z.array(pdfTableCell)).nullable(),
});

export const pdfSection = pdfElement;

export const pdfElementT = z.union([pdfSection, pdfTable]);
export const pdfWord = z.object({
  id: z.string(),
  stringContent: z.string(),
  span: z.tuple([z.number(), z.number()]),
  boundingBox: z
    .tuple([Coordinate, Coordinate, Coordinate, Coordinate])
    .nullable(),
});

const pdfPage = z.object({
  pageNumber: z.number(),
  width: z.number(),
  height: z.number(),
  words: z.array(pdfWord),
});

const pdfDocument = z.object({
  id: z.string(),
  elements: z.array(pdfElementT),
  filename: z.string(),
  markdownContent: z.string(),
  pages: z.array(pdfPage).nullable().optional(),
  widths: z.array(z.number()),
  heights: z.array(z.number()),
  images: z.array(z.string()).nullable(),
});

/**
 * `extractionUrlInfo` when the API points at the presentation copy of the
 * document's elements in blob storage instead of inlining a `pdfDocument`.
 */
export const documentExtractionSignedUrl = z.object({
  url: z.string().url(),
});

const textElement = documentElement;

const textDocument = z.object({
  id: z.string(),
  elements: z.array(textElement),
});

const excelDocument = z.object({
  // Add fields as needed for Excel documents
});

// Video document schemas
const videoConfidence = z.enum(["high", "medium", "low"]);

const videoKeyMoment = z.object({
  timestampSeconds: z.number(),
  actionDescription: z.string(),
  confidence: videoConfidence,
});

const videoMetadata = z.object({
  durationSeconds: z.number(),
  frameRate: z.number(),
  width: z.number(),
  height: z.number(),
  filename: z.string(),
});

const videoChapter = z.object({
  id: z.string(),
  parentId: z.string(),
  stringContent: z.string(),
  title: z.string(),
  description: z.string(),
  startSeconds: z.number(),
  endSeconds: z.number(),
  keyMoments: z.array(videoKeyMoment),
});

const videoDocument = z.object({
  id: z.string(),
  metadata: videoMetadata,
  transcriptSrt: z.string().nullable().optional(),
  summary: z.string(),
  elements: z.array(videoChapter),
});

export type VideoKeyMoment = z.infer<typeof videoKeyMoment>;
export type VideoChapter = z.infer<typeof videoChapter>;
export type VideoMetadata = z.infer<typeof videoMetadata>;
export type VideoDocument = z.infer<typeof videoDocument>;

const parserResult = z.object({
  document: z.union([pdfDocument, excelDocument, videoDocument]),
});

const processedDocument = z.object({
  id: z.string(),
  file: fileData,
  extraction: extractionData,
  labels: z.array(z.string()),
  data: documentData,
  parsedDoc: parserResult.nullable(),
  user: userInfo,
});

const extractionResult = z.array(processedDocument);

const ExtractionSchema = z.object({
  id: z.string(),
  files: z.array(z.string()),
  userId: z.string(),
  extractionId: z.string(),
  status: z.enum(["pending", "processing", "completed", "failed"]),
  workspaceId: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export {
  tableExtractionRow,
  processedDocument,
  ExtractionSchema,
  extractionResult,
  pdfDocument,
  textDocument,
  excelDocument,
  videoDocument,
  videoKeyMoment,
  parserResult,
};
